import api from "./api"


// =========================================================
// HELPERS
// =========================================================

export function normalizeProductionArray(data, preferredKey = "") {
  if (Array.isArray(data)) return data

  if (
    preferredKey &&
    data &&
    Array.isArray(data[preferredKey])
  ) {
    return data[preferredKey]
  }

  if (Array.isArray(data?.items)) return data.items
  if (Array.isArray(data?.data)) return data.data
  if (Array.isArray(data?.results)) return data.results
  if (Array.isArray(data?.boms)) return data.boms
  if (Array.isArray(data?.orders)) return data.orders
  if (Array.isArray(data?.production_orders)) {
    return data.production_orders
  }
  if (Array.isArray(data?.products)) return data.products
  if (Array.isArray(data?.warehouses)) return data.warehouses

  return []
}


// =========================================================
// GESTION DES ERREURS
// =========================================================

export function extractProductionError(
  error,
  fallback = "Une erreur est survenue."
) {
  const detail = error?.response?.data?.detail

  if (typeof detail === "string") {
    return detail
  }

  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        const location = Array.isArray(item?.loc)
          ? item.loc
              .filter((part) => part !== "body")
              .join(" → ")
          : ""

        const message =
          item?.msg || "Erreur de validation"

        return location
          ? `${location} : ${message}`
          : message
      })
      .join(" • ")
  }

  if (detail && typeof detail === "object") {
    return (
      detail.message ||
      detail.detail ||
      JSON.stringify(detail)
    )
  }

  return (
    error?.response?.data?.message ||
    error?.message ||
    fallback
  )
}


// =========================================================
// PAYLOAD BOM
// =========================================================

export function buildProductionBomPayload(form = {}) {
  const finishedProductId = Number(
    form.finished_product_id ??
      form.product_id
  )

  const outputQuantity = Number(
    form.output_quantity ?? 1
  )

  const rawItems = Array.isArray(form.items)
    ? form.items
    : []

  return {
    organization_id:
      form.organization_id !== undefined &&
      form.organization_id !== null &&
      form.organization_id !== ""
        ? Number(form.organization_id)
        : null,

    finished_product_id: finishedProductId,

    name:
      String(form.name || "").trim() ||
      null,

    output_quantity: outputQuantity,

    output_unit:
      form.output_unit ||
      form.unit ||
      null,

    version:
      String(form.version || "1.0").trim() ||
      "1.0",

    notes:
      String(form.notes || "").trim() ||
      null,

    is_active:
      form.is_active !== false,

    items: rawItems.map((item) => ({
      material_product_id: Number(
        item.material_product_id ??
          item.product_id
      ),

      quantity: Number(
        item.quantity ??
          item.required_quantity
      ),

      unit:
        item.unit ||
        null,

      waste_percent: Number(
        item.waste_percent ?? 0
      ),

      notes:
        String(item.notes || "").trim() ||
        null,
    })),
  }
}


// =========================================================
// FORM DATA GLOBAL
// =========================================================

export const getProductionFormData = async () => {
  const response = await api.get(
    "/production/form-data"
  )

  return response.data
}


// =========================================================
// BOM / NOMENCLATURES
// =========================================================

export const getProductionBomFormData = async () => {
  const response = await api.get(
    "/production/boms/form-data"
  )

  return response.data
}


export const getProductionBoms = async () => {
  const response = await api.get(
    "/production/boms"
  )

  return response.data
}


export const getProductionBomById = async (bomId) => {
  const response = await api.get(
    `/production/boms/${bomId}`
  )

  return response.data
}


export const createProductionBom = async (form) => {
  const payload =
    buildProductionBomPayload(form)

  const response = await api.post(
    "/production/boms",
    payload
  )

  return response.data
}


export const updateProductionBom = async (
  bomId,
  form
) => {
  const payload =
    buildProductionBomPayload(form)

  const response = await api.put(
    `/production/boms/${bomId}`,
    payload
  )

  return response.data
}


export const activateProductionBom = async (bomId) => {
  const response = await api.post(
    `/production/boms/${bomId}/activate`,
    {}
  )

  return response.data
}


export const deactivateProductionBom = async (bomId) => {
  const response = await api.post(
    `/production/boms/${bomId}/deactivate`,
    {}
  )

  return response.data
}


// =========================================================
// ORDRES DE PRODUCTION
// =========================================================

export const getProductionOrderFormData = async () => {
  const response = await api.get(
    "/production/orders/form-data"
  )

  return response.data
}


export const getProductionOrders = async () => {
  const response = await api.get(
    "/production/orders"
  )

  return response.data
}


export const getProductionOrderById = async (orderId) => {
  const response = await api.get(
    `/production/orders/${orderId}`
  )

  return response.data
}


export const createProductionOrder = async (payload) => {
  const response = await api.post(
    "/production/orders",
    payload
  )

  return response.data
}


// =========================================================
// RÉSERVATION MATIÈRES
// =========================================================

export const reserveProductionMaterials = async (
  orderId,
  payload = {}
) => {
  const response = await api.post(
    `/production/orders/${orderId}/reserve`,
    payload
  )

  return response.data
}


// =========================================================
// LANCEMENT PRODUCTION
// =========================================================

export const startProductionOrder = async (
  orderId,
  payload = {}
) => {
  const response = await api.post(
    `/production/orders/${orderId}/start`,
    payload
  )

  return response.data
}


// =========================================================
// FIN DE PRODUCTION
// =========================================================

export const completeProductionOrder = async (
  orderId,
  payload
) => {
  const response = await api.post(
    `/production/orders/${orderId}/complete`,
    payload
  )

  return response.data
}


// =========================================================
// ANNULATION
// =========================================================

export const cancelProductionOrder = async (
  orderId,
  payload = {}
) => {
  const response = await api.post(
    `/production/orders/${orderId}/cancel`,
    payload
  )

  return response.data
}